import { useCallback } from 'react';

import { useMutation } from '@lumapps/base-api/react-query';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { error, success } from '@lumapps/notifications';
import { deletePost } from '@lumapps/posts/api';
import { REFRESH_EVENTS } from '@lumapps/posts/constants';
import { POSTS } from '@lumapps/posts/keys';
import { RefreshPosts } from '@lumapps/posts/types';
import { useDispatch } from '@lumapps/redux/react';
import { useInvalidateSpacesQueries } from '@lumapps/spaces/hooks/useInvalidateSpacesQueries';
import { GLOBAL } from '@lumapps/translations';

import { usePostListsRefresh } from './usePostListsRefresh';

export const usePostDelete = (
    postId: string,
    options: { refreshOverride?: RefreshPosts; onSuccess?: () => void } = {},
) => {
    const { refreshOverride, onSuccess } = options;
    const [isDeleteConfirmOpen, , closeDeleteConfirm, openDeleteConfirm] = useBooleanState(false);
    const dispatch = useDispatch();
    const invalidateSpacesQueries = useInvalidateSpacesQueries();
    const refreshPostLists = usePostListsRefresh(refreshOverride);

    const deleteMutation = useMutation({
        mutationFn: () => deletePost(postId),
        onSuccess: () => {
            closeDeleteConfirm();
            // Refetch the space data so its pinned resources no longer include the deleted post
            invalidateSpacesQueries();
            refreshPostLists(REFRESH_EVENTS.POST_DELETE);
            onSuccess?.();
            dispatch(success({ translate: POSTS.POST_DELETE_SUCCESS }));
        },
        onError: () => {
            dispatch(error({ translate: GLOBAL.GENERIC_ERROR }));
            closeDeleteConfirm();
        },
    });

    const { mutate: performDelete } = deleteMutation;

    const onDeletePost = useCallback(() => performDelete(), [performDelete]);

    const deleteConfirmDialogProps = {
        isOpen: isDeleteConfirmOpen,
        isLoading: deleteMutation.isLoading,
        onConfirm: onDeletePost,
        onClose: closeDeleteConfirm,
    };

    return {
        deleteConfirmDialogProps,
        openDeleteConfirm,
    };
};
